import { OrderDto, UpdateOrderDto } from '@/services/dtos'
import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import CommentForm from '@components/forms/CommentForm'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Button, IconButton, Popover, Typography } from '@mui/material'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import {
  clearComments,
  doDeleteComment,
  doFetchCommentsByOrderId,
} from '@state/reducers/CommentReducer'
import { EditIcon, MessageCircle, Plus, Trash } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

interface CommentsPopoverProps {
  createdByFullName: string
  anchorEl: HTMLElement | null
  editingComment: UpdateOrderDto['comment'] | null
  order: OrderDto | null
  commentDialogOpen: boolean
  onClose: () => void
  setOpenCommentDialog: (open: boolean) => void
  setEditingComment: (comment: UpdateOrderDto['comment']) => void
  handleCloseComments: () => void
}

export const CommentsPopoverTrigger = ({
  size = 'medium',
  order,
  handleOpenComments,
}: {
  size?: 'small' | 'medium'
  order: OrderDto
  handleOpenComments: (
    e: React.MouseEvent<HTMLButtonElement>,
    order: OrderDto,
  ) => void
}) => {
  return (
    <IconButton
      onClick={(e) => handleOpenComments(e, order)}
      className="relative"
    >
      {order.commentsCount > 0 && (
        <span
          className={`absolute top-1 right-0.5 bg-red-500 text-white ${
            size === 'small' ? 'text-[10px] w-4' : 'text-[12px] w-5'
          } rounded-full flex items-center justify-center`}
        >
          {order.commentsCount}
        </span>
      )}
      <MessageCircle size={size === 'small' ? 20 : 30} />
    </IconButton>
  )
}

export const CommentsPopover = ({
  createdByFullName,
  anchorEl,
  editingComment,
  onClose,
  order,
  setOpenCommentDialog,
  setEditingComment,
  commentDialogOpen,
  handleCloseComments,
}: CommentsPopoverProps) => {
  const { comments, isLoading } = useAppSelector((state) => state.comment)
  const [commentToDelete, setCommentToDelete] = useState<number | null>(null)
  const dispatch = useAppDispatch()
  const intl = useIntl()

  useEffect(() => {
    if (!order?.id) {
      return
    }
    dispatch(clearComments())
    dispatch(doFetchCommentsByOrderId(order.id)).unwrap()
  }, [order?.id, dispatch])

  const handleDeleteComment = async () => {
    if (!commentToDelete || !order) return
    try {
      await dispatch(
        doDeleteComment({
          commentId: commentToDelete,
          orderId: order.id,
        }),
      ).unwrap()

      dispatch(
        enqueueSnackbar({
          message: intl.formatMessage({ id: 'member-comment-deleted' }),
          type: 'success',
        }),
      )

      setCommentToDelete(null)
    } catch (error) {
      console.error('Error deleting comment:', error)
      dispatch(
        enqueueSnackbar({
          message: intl.formatMessage({ id: 'error-deleting-comment' }),
          type: 'error',
        }),
      )
    }
  }

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        TransitionProps={{
          onExited: handleCloseComments,
        }}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="flex justify-end p-2">
          <Button
            disabled={isLoading}
            variant="outlined"
            onClick={() => setOpenCommentDialog(true)}
            startIcon={<Plus size={18} />}
          >
            Ajouter un commentaire
          </Button>
        </div>
        <div className="flex flex-col min-w-[200px] max-w-[700px] gap-4 p-2 max-h-[400px] overflow-auto">
          {comments?.length > 0 ? (
            comments?.map((comment) => (
              <div
                key={comment.id}
                className="flex justify-between items-start gap-2 px-2 w-[550px]"
              >
                <div className="flex flex-col gap-1">
                  <Typography variant="body1" fontWeight="medium">
                    {createdByFullName} :
                  </Typography>
                  <Typography variant="body2" className="px-1 max-w-[400px]">
                    {comment.content}
                  </Typography>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <div className="flex items-center">
                    <IconButton
                      disabled={isLoading}
                      size="small"
                      color="info"
                      onClick={() => {
                        setEditingComment(comment)
                        setOpenCommentDialog(true)
                      }}
                    >
                      <EditIcon size={18} />
                    </IconButton>
                    <IconButton
                      disabled={isLoading}
                      size="small"
                      color="error"
                      onClick={() => {
                        setCommentToDelete(comment.id)
                      }}
                    >
                      <Trash size={18} />
                    </IconButton>
                  </div>
                  <Typography variant="caption">
                    {new Date(comment.createdAt).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </div>
              </div>
            ))
          ) : (
            <Typography
              variant="body2"
              className="p-2"
              alignSelf="center"
              color="text.secondary"
            >
              Aucun commentaire
            </Typography>
          )}
        </div>
        <DeleteConfirmationDialog
          open={Boolean(commentToDelete)}
          onClose={() => setCommentToDelete(null)}
          onConfirm={handleDeleteComment}
          title="Supprimer le commentaire"
          description="Voulez-vous vraiment supprimer ce commentaire ?"
        />
      </Popover>
      <CommentForm
        editingComment={editingComment}
        orderId={order?.id}
        open={commentDialogOpen}
        onClose={() => {
          setOpenCommentDialog(false)
          setEditingComment(undefined)
        }}
      />
    </>
  )
}
